import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useMsal } from '@azure/msal-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/ui-kit/molecules/modal';
import { Button } from '@/ui-kit/atoms/button';
import { RequiredMark } from '@/ui-kit/atoms/required-mark';
import { FieldLabel } from './field-label';
import { apiJson, type HttpError } from '@/app-kit/request-service';
import { buildDefaults, buildFieldGroupPatch, initialValue, type FieldGroupValues } from './field-group-patch';
import { maskLabel } from './access-map';

/**
 * Config-driven edit modal for a field-group on any dx_version-locked
 * entity. Generalises the customer summary editors: a small
 * `FieldGroupConfig` plus a PATCH `endpoint` is all a surface needs, and
 * this owns the shared optimistic-lock + 409 conflict-diff machinery.
 *
 * The endpoint must accept `{ patch, dx_version, dx_reason }` and, on 409,
 * return `{ current_row, current_version }` (the live customer / bank /
 * site routes all do). Used by the Summary, Bank, and Site surfaces.
 */

export type EditFieldSpec = {
  /** column name; sent verbatim in the patch (route writes it as the column). */
  name:    string;
  label:   string;
  type:    'text' | 'date' | 'checkbox' | 'select';
  /**
   * Must be non-empty (text and select). A required select offers no empty
   * "—" option once the record has a value — bug da2932e6 saved "Payable
   * type" empty and the NOT NULL column 500'd. It keeps "—" only when the
   * record has no value, so the browser cannot silently pick the first option.
   */
  required?: boolean;
  /**
   * select options. `numeric` sends the value as a number. View mode shows an
   * option's label for an empty value when an option is valued `''`.
   */
  options?: { value: string; label: string }[];
  /** A select that never offers the generic empty "—": its options cover every value. */
  noEmptyOption?: boolean;
  numeric?: boolean;
  /**
   * A select over a boolean column: options valued `'true'` / `'false'`, sent
   * as a real boolean. The server refuses the string (it would be cast).
   */
  boolean?: boolean;
  /**
   * Show the field's value but don't let it be edited (#915).
   *
   * Every block's modal lists ALL of that block's fields, so the modal is a
   * complete picture of the block rather than a subset — but some fields
   * genuinely cannot be written: engine-owned (rank), `customer.minimum`
   * (status / customer type — the server refuses the patch for every role),
   * or system-assigned (customer id).
   *
   * Read-only fields render as static text and are **excluded from the
   * patch body** — not merely disabled in the DOM. That matters: a disabled
   * input still round-trips through the form, and sending an unchanged
   * column would both fail `validateCustomerPatch` and write a spurious
   * change-log entry.
   */
  readOnly?: boolean;
  /**
   * The server withheld this field's value from the caller (`_access` masked).
   * It renders as the mask placeholder, never as `—`, which means "empty".
   * Set by `applyAccess`; always paired with `readOnly`.
   */
  masked?: boolean;
};

export type FieldGroupConfig = {
  title:  string;
  fields: EditFieldSpec[];
  /** Lay fields out in N columns (default 1). Widens the modal when > 1. */
  columns?: 1 | 2;
  /**
   * Optional caution shown under the title, before the fields. For groups
   * where a field has a consequence the operator cannot see from the form —
   * e.g. Key Dates, where `date1` feeds the VCAMP fee calculation (#1078).
   * Keep it to one sentence; it is a warning, not help text.
   */
  description?: string;
  /** Optional content centred in the modal header — e.g. a status mark. */
  headerCenter?: ReactNode;
  /**
   * Optional panel under the fields, above the reason — e.g. the address
   * Verify button. It sees the form's live values, can fill fields, and can
   * add columns to the save that are not fields of the dialog.
   */
  renderExtra?: (ctx: FieldGroupExtraContext) => ReactNode;
};

export type FieldGroupExtraContext = {
  /** The form's current values. */
  values: FieldGroupValues;
  /** Fill fields, as if the operator typed the values. */
  setValues: (values: Record<string, string>) => void;
  /**
   * Columns to add to the save, or null for none. Read when Save is pressed;
   * a column here is sent even when no field changed.
   */
  setExtraPatch: (patch: Record<string, unknown> | null) => void;
};

type Entity = Record<string, unknown> & { dx_version?: number };
type ConflictBody = {
  error:           string;
  // Absent on a create-collision 409 (#1026): there was no row to return a
  // "current" version of — another operator simply got there first.
  current_row?:     Record<string, unknown> & { dx_version: number };
  current_version?: number;
};

type Props = {
  open:     boolean;
  onClose:  () => void;
  entity:   Entity;
  /** Full PATCH path, e.g. `/api/customers/123` or `/api/customers/123/bank-details`. */
  endpoint: string;
  /** react-query keys to invalidate after a successful save / reload. */
  invalidateKeys?: unknown[][];
  config:   FieldGroupConfig;
  /** Open read-only first (a quick-look view with an Edit option), like the bill
   *  modal — used by id-click modals (e.g. the order modal). Default false =
   *  open straight into the edit form (the per-section "Edit" buttons). */
  viewFirst?: boolean;
  /** Whether to offer the Edit button in view mode (gate on the caller's
   *  `<x>:edit` privilege). Only consulted when `viewFirst`. Default true. */
  canEdit?: boolean;
  /**
   * Create mode (#1026): the entity does not exist yet and this save will
   * insert it. Sends `dx_version: 0` — "I saw no row". If another editor has
   * created the row since, 0 cannot match it, so the server answers 409 with
   * that row instead of writing over it. A missing version would mean a bulk
   * upload line, which needs the `start_bulk_job` grant and never creates a
   * row (404).
   *
   * Passing an empty `entity` without this flag would send `dx_version: 1` —
   * a false claim about a row that doesn't exist — so create surfaces must set
   * it explicitly.
   */
  isCreate?: boolean;
};

/** Read-only display of a field's value (resolves selects to their label). */
function displayValue(field: EditFieldSpec, entity: Entity): string {
  const raw = entity[field.name];
  if (field.type === 'checkbox') return raw ? 'Yes' : 'No';
  if (field.type === 'select') {
    const option = field.options?.find((o) => o.value === String(raw ?? ''));
    if (option) return option.label;
  }
  if (raw == null || raw === '') return '—';
  return String(raw);
}

type FormValues = FieldGroupValues;

export function EditFieldGroupModal({ open, onClose, entity, endpoint, invalidateKeys = [], config, viewFirst = false, canEdit = true, isCreate = false }: Props) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  const [snapshot, setSnapshot] = useState<Entity | null>(null);
  const [conflict, setConflict] = useState<ConflictBody | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>(viewFirst ? 'view' : 'edit');
  // A ref, not state: the extra panel sets it on every render, and a state
  // update there would render again without end.
  const extraPatch = useRef<Record<string, unknown> | null>(null);

  // Two-column modals fill VERTICALLY (#915): fields run down the left
  // column, then continue down the right — matching the summary blocks
  // they edit. Plain `grid-cols-2` fills row-by-row, which would put
  // field 2 beside field 1 instead of beneath it; `grid-flow-col` plus an
  // explicit row count gives column-first order. Odd counts put the extra
  // field in the LEFT column (ceil).
  const columnRows = Math.ceil(config.fields.length / 2);
  const firstEditableFieldName = config.fields.find((f) => !f.readOnly)?.name;

  const form = useForm<FormValues>({
    mode:          'onChange',
    defaultValues: buildDefaults(config, entity),
  });

  // Re-seed the form + snapshot whenever the modal (re)opens or the entity
  // version changes.
  useEffect(() => {
    if (!open) return;
    setSnapshot(entity);
    setConflict(null);
    setMode(viewFirst ? 'view' : 'edit');
    extraPatch.current = null;
    form.reset(buildDefaults(config, entity));
    // config is a stable per-surface constant; entity drives resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endpoint, entity.dx_version]);

  const update = useMutation<unknown, HttpError, { patch: Record<string, unknown>; dx_version?: number; dx_reason: string }>({
    mutationFn: (body) => apiJson(instance, endpoint, { method: 'PATCH', body: JSON.stringify(body) }),
    meta: { silent: true }, // 409s are expected; the modal surfaces them
  });

  const invalidateAll = () => invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));

  const reloadAndClose = () => {
    update.reset();
    setConflict(null);
    invalidateAll();
    onClose();
  };

  const onSubmit = (values: FormValues) => {
    setConflict(null);
    const reason = String(values.dx_reason ?? '').trim();
    // What to send — read-only fields never, an edit only what changed:
    // `buildFieldGroupPatch`.
    const fieldPatch = buildFieldGroupPatch(config, entity, values, isCreate);
    const patch = extraPatch.current ? { ...(fieldPatch ?? {}), ...extraPatch.current } : fieldPatch;
    if (patch === null) {
      onClose(); // nothing changed — nothing to save
      return;
    }

    // Create sends dx_version 0 — see the `isCreate` prop.
    update.mutate(
      { patch, dx_version: isCreate ? 0 : (entity.dx_version ?? 1), dx_reason: reason },
      {
        onSuccess: () => { invalidateAll(); onClose(); },
        onError: (err) => {
          if (err.status === 409) {
            const body = err.body as ConflictBody | undefined;
            if (body) setConflict(body);
          }
        },
      },
    );
  };

  const reasonValid = String(form.watch('dx_reason') ?? '').trim().length >= 10;

  const conflictDiff = useMemo(() => {
    // Bound to a local so the narrowing survives into the map closure —
    // current_row is optional since #1026 (create-collision 409s omit it).
    const currentRow = conflict?.current_row;
    if (!currentRow || !snapshot) return null;
    return config.fields
      .map((f) => ({
        field:    f.label,
        snapshot: snapshot[f.name],
        current:  currentRow[f.name],
      }))
      .filter((d) => String(d.snapshot ?? '') !== String(d.current ?? ''));
  }, [conflict, snapshot, config]);

  return (
    <Modal
      open={open}
      onClose={() => { update.reset(); onClose(); }}
      title={config.title}
      headerCenter={config.headerCenter}
      size={config.columns === 2 ? 'max-w-2xl' : 'max-w-lg'}
      footer={
        conflict ? (
          <Button onClick={reloadAndClose}>Reload</Button>
        ) : mode === 'view' ? (
          <>
            {canEdit && <Button onClick={() => setMode('edit')}>Edit</Button>}
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => (viewFirst ? setMode('view') : onClose())} disabled={update.isPending}>Cancel</Button>
            <Button onClick={form.handleSubmit(onSubmit)} disabled={update.isPending || !reasonValid}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        )
      }
    >
      {conflict ? (
        <div className="space-y-3">
          {/* A create-collision 409 may carry no current_row (two creates at
              the same moment), and then there is no Was/Now diff to show,
              only the fact of it. */}
          <p className="text-sm text-amber-800">
            {isCreate
              ? 'Another editor created this record first. Your draft can’t be saved.'
              : 'This record changed under you. Your draft can’t be saved.'}
          </p>
          {conflictDiff && conflictDiff.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs">
              <p className="mb-2 font-medium text-amber-900">Fields changed by another editor:</p>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-amber-800">
                    <th className="py-1 pr-2">Field</th><th className="py-1 pr-2">Was</th><th className="py-1">Now</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictDiff.map((d) => (
                    <tr key={d.field} className="border-t border-amber-100">
                      <td className="py-1 pr-2 font-mono">{d.field}</td>
                      <td className="py-1 pr-2 text-gray-700">{fmt(d.snapshot)}</td>
                      <td className="py-1 text-gray-900">{fmt(d.current)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-500">Click <strong>Reload</strong> to discard your draft and view the current values.</p>
        </div>
      ) : mode === 'view' ? (
        <dl
          className={config.columns === 2 ? 'grid grid-flow-col grid-cols-2 gap-x-6 gap-y-3 text-sm' : 'space-y-2 text-sm'}
          style={config.columns === 2 ? { gridTemplateRows: `repeat(${columnRows}, minmax(0, 1fr))` } : undefined}
        >
          {config.fields.map((f) => (
            <div key={f.name}>
              <dt className="text-xs font-medium text-gray-500"><FieldLabel name={f.name}>{f.label}</FieldLabel></dt>
              <dd className="text-gray-900">{displayValue(f, entity)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {config.description && (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {config.description}
            </p>
          )}
          <div
            className={config.columns === 2 ? 'grid grid-flow-col grid-cols-2 gap-x-4 gap-y-3' : 'space-y-4'}
            style={config.columns === 2 ? { gridTemplateRows: `repeat(${columnRows}, minmax(0, 1fr))` } : undefined}
          >
            {/* autoFocus targets the first EDITABLE field — with read-only
                fields in the list (#915) the first field may not be one. */}
            {config.fields.map((f) => (
              <FieldRow
                key={f.name}
                field={f}
                register={form.register}
                autoFocus={f.name === firstEditableFieldName}
                entity={entity}
                error={form.formState.errors[f.name]?.message as string | undefined}
              />
            ))}
          </div>
          {config.renderExtra?.({
            values:        form.watch(),
            setValues:     (vals) => {
              for (const [k, v] of Object.entries(vals)) {
                form.setValue(k, v, { shouldDirty: true, shouldValidate: true });
              }
            },
            setExtraPatch: (p) => { extraPatch.current = p; },
          })}
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Reason for change<RequiredMark /></span>
            <textarea
              {...form.register('dx_reason', {
                validate: (v) => String(v ?? '').trim().length >= 10 || 'reason must be at least 10 characters',
              })}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 outline-none focus:border-brand-500"
              rows={2}
              placeholder="Why is this changing? (min 10 characters)"
            />
            {form.formState.errors.dx_reason && (
              <span className="mt-1 block text-xs text-red-700">{String(form.formState.errors.dx_reason.message)}</span>
            )}
          </label>

          {update.isError && update.error.status !== 409 && (
            <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">{update.error.message}</div>
          )}
          {isCreate && (
            <p className="text-xs text-gray-500">
              This will create a new record. Fields left blank are saved empty and can be filled in later.
            </p>
          )}
        </form>
      )}
    </Modal>
  );
}

function fmt(v: unknown): React.ReactNode {
  if (v == null || v === '') return <em>∅</em>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function FieldRow({
  field, register, autoFocus, entity, error,
}: {
  field: EditFieldSpec;
  register: ReturnType<typeof useForm<FormValues>>['register'];
  autoFocus: boolean;
  entity: Entity;
  error?: string;
}) {
  const base = 'mt-1 w-full rounded border border-gray-300 px-2 py-1 outline-none focus:border-brand-500';

  // Read-only (#915): show the value as static text, styled to read as
  // "not an input" rather than a greyed-out one. Deliberately NOT a
  // `disabled` input — see the `readOnly` note on EditFieldSpec.
  if (field.readOnly) {
    const shown = field.masked ? maskLabel(undefined) : displayValue(field, entity);
    return (
      <div className="block">
        <span className="text-xs font-medium text-gray-500"><FieldLabel name={field.name}>{field.label}</FieldLabel></span>
        <p className="mt-1 px-2 py-1 text-sm text-gray-700">
          {shown === '' ? <span className="text-gray-300">—</span> : shown}
          <span className="ml-2 text-xs text-gray-400">(read-only)</span>
        </p>
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-2">
        <input type="checkbox" {...register(field.name)} className="rounded border-gray-300" />
        <span className="text-xs font-medium text-gray-700"><FieldLabel name={field.name}>{field.label}</FieldLabel></span>
      </label>
    );
  }
  const rules = field.required ? { required: `${field.label} required` } : undefined;
  const offerEmpty = !field.noEmptyOption && (!field.required || initialValue(field, entity) === '');
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700"><FieldLabel name={field.name}>{field.label}</FieldLabel></span>
      {field.type === 'select' ? (
        <select {...register(field.name, rules)} className={base} autoFocus={autoFocus}>
          {offerEmpty && <option value="">—</option>}
          {(field.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          type={field.type === 'date' ? 'date' : 'text'}
          {...register(field.name, rules)}
          className={base}
          autoFocus={autoFocus}
        />
      )}
      {error && <span className="mt-1 block text-xs text-red-700">{error}</span>}
    </label>
  );
}
